import { motion, AnimatePresence } from "framer-motion";
import { SideSheet, ConfirmDialog, ActivityFeed } from "@/components/ui";
import { useTranslation } from "@/packages/utils";
import { RenameDocumentDialog } from "./RenameDocumentDialog";
import { TagSelectorSheet } from "./TagSelectorSheet";
import { useDocumentDetail } from "./hooks/use-document-detail";
import { DocumentDetailHeader } from "./DocumentDetailHeader";
import { DocumentDetailContent } from "./DocumentDetailContent";
import { DocumentDetailLinkedItems } from "./DocumentDetailLinkedItems";
import { DocumentLinkedChecklist } from "./DocumentLinkedChecklist";
import { DocumentDetailFooter } from "./DocumentDetailFooter";
import { DocumentDetailChat } from "./DocumentDetailChat";
import { ChecklistSuggestions } from "./upload/ChecklistSuggestions";
import { SuggestionHistory } from "./SuggestionHistory";
import { useChecklistSuggestions } from "@/hooks/use-checklist-suggestions";
import { useLinkedChecklistItems } from "@/hooks/use-linked-checklist-items";
import { useWorkspaceQuery } from "@/hooks";
import { api } from "convex/_generated/api";
import { AlertTriangle, Loader2, RotateCcw, Search } from "lucide-react";
import { Button } from "@/components/ui/primitives";
import { toast } from "sonner";
import { useState } from "react";
import { AuditSelectSheet } from "../audits/AuditSelectSheet";
import type { DocumentView } from "./types";
import type { AuditId, Id } from "@/packages/types";

interface DocumentDetailSheetProps {
  open: boolean;
  onClose: () => void;
  document: DocumentView | null;
}

type Tab = "details" | "suggestions" | "checklist" | "activity" | "chat";

export function DocumentDetailSheet({
  open,
  onClose,
  document,
}: DocumentDetailSheetProps) {
  const { t } = useTranslation(["common", "documents"]);
  const detailState = useDocumentDetail(document);

  const documentId = document?.id ? (document.id as Id<"documents">) : null;
  const {
    suggestions,
    suggestedChecklistItemsStatus,
    retryMatching,
    isRetrying,
  } = useChecklistSuggestions(documentId);
  const suggestionsCount = suggestions.length;
  const { items: linkedChecklistItems } = useLinkedChecklistItems(documentId);
  const linkedChecklistCount = linkedChecklistItems.length;

  const { data: suggestionHistoryData } = useWorkspaceQuery(
    api.document_queries.getSuggestionHistory,
    { documentId: documentId ?? null },
  );
  const historyCount = suggestionHistoryData?.length ?? 0;

  const [showAuditPicker, setShowAuditPicker] = useState(false);

  const handleFindMatches = async (auditId: AuditId) => {
    if (!documentId) return;
    try {
      await retryMatching(documentId, auditId as Id<"audits">);
    } catch {
      toast.error(t("documents:detail.retryFailed"));
    }
  };

  if (!document) return null;

  // Resolve aiSummary with fallback
  const aiSummary =
    document.aiSummary ??
    (document.description
      ? {
          text: document.description,
          status: "ready" as const,
          wasTruncated: false,
        }
      : undefined);

  const handleTagEditorSave = async (tagIds: string[]) => {
    await detailState.handleTagEditorSave(tagIds);
  };

  const handleDelete = async () => {
    const success = await detailState.handleDelete();
    if (success) {
      onClose();
    }
  };

  const handleNavigateToChecklistItem = (
    checklistItemId: string,
    auditId: string | null,
  ) => {
    // Don't call onClose — it navigates back to the documents list,
    // which would overwrite the checklist-item navigation.
    // The route change itself will unmount this sheet.
    detailState.handleNavigateToChecklistItem(checklistItemId, auditId);
  };

  const getTabLabel = (tabId: Tab): string => {
    const labels: Record<Tab, string> = {
      details: t("documents:detail.tabs.details"),
      suggestions: t("documents:detail.tabs.suggestions"),
      checklist: t("documents:detail.tabs.checklist"),
      activity: t("documents:detail.tabs.activity"),
      chat: t("documents:detail.tabs.chat"),
    };
    return labels[tabId];
  };

  const tabs: { id: Tab; label: string; count?: number }[] = [
    { id: "details", label: getTabLabel("details") },
    {
      id: "suggestions" as Tab,
      label: getTabLabel("suggestions"),
      count:
        suggestionsCount > 0
          ? suggestionsCount
          : historyCount > 0
            ? historyCount
            : undefined,
    },
    {
      id: "checklist" as Tab,
      label: getTabLabel("checklist"),
      count: linkedChecklistCount > 0 ? linkedChecklistCount : undefined,
    },
    {
      id: "activity",
      label: getTabLabel("activity"),
      count:
        detailState.activities.length > 0
          ? detailState.activities.length
          : undefined,
    },
    { id: "chat" as Tab, label: getTabLabel("chat") },
  ];

  return (
    <>
      {/* Hidden file input for version upload */}
      <input
        ref={detailState.fileInputRef}
        type="file"
        className="hidden"
        accept={`.${document.type}`}
        onChange={detailState.handleFileSelected}
      />

      <SideSheet open={open} onClose={onClose}>
        {/* Header */}
        <DocumentDetailHeader
          document={document}
          onUploadNewVersion={detailState.handleUploadNewVersion}
          onRename={() => detailState.setShowRenameDialog(true)}
          onDelete={() => detailState.setShowDeleteConfirm(true)}
          onDownloadPreviousVersion={detailState.downloadPreviousVersion}
          hasPreviousVersion={detailState.hasPreviousVersion}
        />

        {/* Tab bar */}
        <div className="px-6">
          <div className="flex items-center gap-1 border-b border-border">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => detailState.setActiveTab(tab.id)}
                className={`relative px-3 py-2.5 text-[13px] font-medium transition-colors cursor-pointer ${
                  detailState.activeTab === tab.id
                    ? "text-primary"
                    : "text-muted hover:text-secondary"
                }`}
              >
                <span className="flex items-center gap-1.5">
                  {tab.label}
                  {tab.count !== undefined && (
                    <span
                      className={`text-[11px] tabular-nums ${
                        detailState.activeTab === tab.id
                          ? "text-accent"
                          : "text-muted"
                      }`}
                    >
                      {tab.count}
                    </span>
                  )}
                </span>
                {detailState.activeTab === tab.id && (
                  <motion.div
                    layoutId="detail-tab-indicator"
                    className="absolute bottom-0 left-0 right-0 h-[2px] bg-accent rounded-full"
                    transition={{
                      type: "spring",
                      stiffness: 380,
                      damping: 30,
                    }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Body */}
        <SideSheet.Body>
          <AnimatePresence mode="wait">
            {/* Details tab */}
            {detailState.activeTab === "details" && (
              <motion.div
                key="details"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15 }}
                className="space-y-5"
              >
                <DocumentDetailContent
                  document={document}
                  aiSummary={aiSummary}
                  isUploading={detailState.isUploading}
                  viewInNewTab={detailState.viewInNewTab}
                  isDownloadLoading={detailState.isDownloadLoading}
                  onSwitchToSuggestionsTab={
                    suggestionsCount > 0
                      ? () => detailState.setActiveTab("suggestions")
                      : undefined
                  }
                  onCategoryUpdate={detailState.handleCategoryUpdate}
                  onExpirationDateUpdate={
                    detailState.handleExpirationDateUpdate
                  }
                  onEditTags={() => detailState.setShowTagEditor(true)}
                />

                <DocumentDetailLinkedItems
                  documentId={documentId as Id<"documents">}
                  linkedChecklistCount={linkedChecklistCount}
                  onSwitchToChecklistTab={() =>
                    detailState.setActiveTab("checklist")
                  }
                />
              </motion.div>
            )}

            {/* Suggestions tab */}
            {detailState.activeTab === "suggestions" && documentId && (
              <motion.div
                key="suggestions"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15 }}
              >
                {/* Generating state */}
                {(suggestedChecklistItemsStatus === "pending" ||
                  suggestedChecklistItemsStatus === "generating") && (
                  <div className="flex flex-col items-center gap-3 py-12 text-center">
                    <Loader2 size={24} className="animate-spin text-accent" />
                    <p className="text-sm text-muted">
                      {t("documents:detail.suggestions.generating")}
                    </p>
                  </div>
                )}

                {/* Failed state */}
                {suggestedChecklistItemsStatus === "failed" && (
                  <div className="flex flex-col items-center gap-3 py-6 text-center">
                    <AlertTriangle className="text-destructive" size={20} />
                    <p className="text-sm text-muted-foreground">
                      Matching failed.
                    </p>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={isRetrying}
                      onClick={() => setShowAuditPicker(true)}
                    >
                      {isRetrying ? (
                        <Loader2 size={14} className="animate-spin mr-1.5" />
                      ) : (
                        <RotateCcw size={14} className="mr-1.5" />
                      )}
                      {t("documents:actions.retry")}
                    </Button>
                  </div>
                )}

                {/* Results state */}
                {suggestionsCount > 0 && (
                  <div className="space-y-4">
                    <ChecklistSuggestions
                      documentId={documentId}
                      onComplete={() => detailState.setActiveTab("details")}
                    />
                    <div className="pt-2 border-t border-border">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setShowAuditPicker(true)}
                        className="w-full"
                      >
                        <Search size={14} className="mr-1.5" />
                        {t("documents:detail.suggestions.findMatches")}
                      </Button>
                    </div>
                  </div>
                )}

                {/* History state — show when no pending suggestions but history exists */}
                {suggestionsCount === 0 &&
                  historyCount > 0 &&
                  suggestedChecklistItemsStatus !== "pending" &&
                  suggestedChecklistItemsStatus !== "generating" &&
                  suggestedChecklistItemsStatus !== "failed" && (
                    <div className="space-y-4">
                      <SuggestionHistory documentId={documentId} />
                      <div className="pt-2 border-t border-border">
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => setShowAuditPicker(true)}
                          className="w-full"
                        >
                          <Search size={14} className="mr-1.5" />
                          {t("documents:detail.suggestions.findMatches")}
                        </Button>
                      </div>
                    </div>
                  )}

                {/* Empty state */}
                {suggestionsCount === 0 &&
                  historyCount === 0 &&
                  suggestedChecklistItemsStatus !== "pending" &&
                  suggestedChecklistItemsStatus !== "generating" &&
                  suggestedChecklistItemsStatus !== "failed" && (
                    <div className="flex flex-col items-center gap-3 py-12 text-center">
                      <Search size={24} className="text-muted" />
                      <div>
                        <p className="text-sm font-medium text-secondary">
                          {t("documents:detail.suggestions.emptyTitle")}
                        </p>
                        <p className="text-xs text-muted mt-1">
                          {t("documents:detail.suggestions.emptyDescription")}
                        </p>
                      </div>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => setShowAuditPicker(true)}
                      >
                        <Search size={14} className="mr-1.5" />
                        {t("documents:detail.suggestions.findMatches")}
                      </Button>
                    </div>
                  )}
              </motion.div>
            )}

            {/* Checklist tab */}
            {detailState.activeTab === "checklist" && documentId && (
              <motion.div
                key="checklist"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15 }}
              >
                <DocumentLinkedChecklist
                  documentId={documentId}
                  onNavigateToItem={handleNavigateToChecklistItem}
                />
              </motion.div>
            )}

            {/* Activity tab */}
            {detailState.activeTab === "activity" && (
              <ActivityFeed
                items={detailState.activities}
                emptyTitle="No activity yet"
                emptyDescription="Activity will appear here as changes are made to this document."
              />
            )}

            {/* AI Chat tab — negative margins cancel SideSheet.Body padding so chat fills edge-to-edge */}
            {detailState.activeTab === "chat" && (
              <motion.div
                key="chat"
                initial={{ opacity: 0, y: 4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.15 }}
                className="flex flex-col h-full -mx-6 -mt-4"
              >
                <DocumentDetailChat document={document} />
              </motion.div>
            )}
          </AnimatePresence>
        </SideSheet.Body>

        {/* Footer — hidden on chat tab */}
        {detailState.activeTab !== "chat" && (
          <DocumentDetailFooter
            onDownload={detailState.download}
            isDownloadLoading={detailState.isDownloadLoading}
            authorName={
              detailState.resolvedAuthorName ??
              document.author?.name ??
              "Unknown"
            }
            authorEmail={detailState.resolvedAuthorEmail ?? undefined}
            authorAvatarUrl={document.author?.avatarUrl}
            authorAvatarColor={document.author?.avatarColor}
          />
        )}
      </SideSheet>

      {/* Rename dialog */}
      <RenameDocumentDialog
        open={detailState.showRenameDialog}
        onClose={() => detailState.setShowRenameDialog(false)}
        documentId={document.id}
        currentName={document.title}
      />

      {/* Delete confirmation dialog */}
      <ConfirmDialog
        open={detailState.showDeleteConfirm}
        onClose={() => detailState.setShowDeleteConfirm(false)}
      >
        <ConfirmDialog.Title>
          {t("documents.deleteDocument")}
        </ConfirmDialog.Title>
        <ConfirmDialog.Body>
          <p className="text-[13px] text-secondary">
            {t("documents.deleteConfirm", { name: document.title })}
          </p>
        </ConfirmDialog.Body>
        <ConfirmDialog.Actions
          onConfirm={handleDelete}
          onCancel={() => detailState.setShowDeleteConfirm(false)}
          confirmLabel={t("buttons.delete")}
          cancelLabel={t("buttons.cancel")}
          variant="danger"
          loading={detailState.isDeleting}
        />
      </ConfirmDialog>

      <TagSelectorSheet
        open={detailState.showTagEditor}
        onClose={() => detailState.setShowTagEditor(false)}
        documentTags={document?.tags}
        documentName={document?.title}
        onSave={handleTagEditorSave}
      />

      <AuditSelectSheet
        open={showAuditPicker}
        onClose={() => setShowAuditPicker(false)}
        title={t("documents:detail.suggestions.selectAuditTitle")}
        description={t("documents:detail.suggestions.selectAuditDescription")}
        onSelect={handleFindMatches}
        closeOnSelect
      />
    </>
  );
}
